import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Tag, Avatar, Tooltip, Dropdown, Button, Menu } from 'antd';
import { EllipsisOutlined, ClockCircleOutlined } from '@ant-design/icons';

export const SortableTask = ({ task, listId }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: `task-${task.id}`,
        data: {
            type: 'task',
            task,
            sortable: {
                containerId: listId // Ensure this is set correctly
            }
        },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 2: return 'red';
            case 1: return 'orange';
            default: return 'green';
        }
    };

    const getPriorityText = (priority) => {
        switch (priority) {
            case 2: return 'High';
            case 1: return 'Medium';
            default: return 'Low';
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="mb-2 cursor-grab active:cursor-grabbing"
        >
            <Card
                size="small"
                className="bg-white shadow-sm hover:shadow-md transition-shadow"
                bordered={false}
            >
                <div className="flex flex-col">
                    {/* Task Header */}
                    <div className="flex justify-between items-start mb-2">
                        <h4 className="text-sm font-medium m-0">{task.title}</h4>
                        <Dropdown overlay={
                            <Menu>
                                <Menu.Item key="edit">Edit</Menu.Item>
                                <Menu.Item key="delete" danger>Delete</Menu.Item>
                            </Menu>
                        } trigger={['click']}>
                            <Button
                                type="text"
                                size="small"
                                icon={<EllipsisOutlined />}
                                className="hover:bg-gray-100"
                            />
                        </Dropdown>
                    </div>

                    {/* Labels */}
                    {task.labels?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                            {task.labels.map(label => (
                                <Tag
                                    key={label.id}
                                    color={label.color}
                                    className="m-0"
                                >
                                    {label.name}
                                </Tag>
                            ))}
                        </div>
                    )}

                    {/* Status */}
                    {task.status && (
                        <Tag
                            color={task.status.color}
                            className="mb-2 w-fit"
                        >
                            {task.status.name}
                        </Tag>
                    )}

                    {/* Task Footer */}
                    <div className="flex justify-between items-center mt-1">
                        <div className="flex items-center gap-2">
                            {/* Priority */}
                            <Tag
                                color={getPriorityColor(task.priority)}
                                className="m-0"
                            >
                                {getPriorityText(task.priority)}
                            </Tag>

                            {/* Due Date */}
                            {task.due_date && (
                                <Tooltip title="Due Date">
                                    <span className="flex items-center text-xs text-gray-500">
                                        <ClockCircleOutlined className="mr-1" />
                                        {new Date(task.due_date).toLocaleDateString()}
                                    </span>
                                </Tooltip>
                            )}
                        </div>

                        {/* Assignee */}
                        {task.assignee && (
                            <Tooltip title={task.assignee.name}>
                                <Avatar
                                    size="small"
                                    className="bg-blue-500"
                                >
                                    {task.assignee.name.charAt(0).toUpperCase()}
                                </Avatar>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
};
